import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as CD from 'app/cd/models/essette-api';

interface PharmacyClaimWithPerDay extends CD.PharmacyClaim {
    perDay?: string;
}

@autoinject()
export class MemberPharmacy extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected member: CD.Member | null = null;
    protected pharmacyClaims: ReadonlyArray<PharmacyClaimWithPerDay> = [];

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['perDay'];
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected openProviderSummary = openProviderSummary;
    protected showProviderSummaryLink: boolean = false;
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.apiSorting.entries = [
            {
                reference: 'serviceDate',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.checkPermissions();
        this.loadCustomFieldTargets();
        this.loadPharmacyClaims();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadPharmacyClaims();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const pharmacyClaims: CD.PharmacyClaim[] = <CD.PharmacyClaim[]>this.pharmacyClaims;
            if(direction === null) {
                this.localSorting.remove(property);
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending,
                    convert: function(a: any) {
                        return parseFloat(a); //perDay is numeric
                    }
                });
            }
            localSort(pharmacyClaims, localSorting);
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.PharmacyClaim');
    }

    // add calculated perDay property to each claim
    private calculatePerDays(): void {
        if(this.pharmacyClaims.length === 0) return;

        this.pharmacyClaims.forEach(claim => {
            const { quantity, daysSupply } = claim;
            if(!daysSupply || !quantity) {
                claim.perDay = 'N/A';
            } else {
                const perDay = ((quantity / daysSupply).toFixed(2)).toString();
                const [beforeDec, afterDec] = perDay.split('.');
                claim.perDay = afterDec === '00' ? beforeDec : perDay;
            }
        });
    }

    private configurePharmacyPhoneNumbers(): void {
        this.pharmacyClaims.forEach(claim => {
            const primary = claim.pharmacy?.phoneNumber?.primary;
            const fax = claim.pharmacy?.phoneNumber?.fax;
            if(primary?.number && primary.number.length === 10) {
                primary.$display = `+1 ${primary.$display}`;
            }
            if(fax?.number && fax.number.length === 10) {
                fax.$display = `+1 ${fax.$display}`;
            }
            return;
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadPharmacyClaims(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.apiSorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes: string[] = [
            'drug',
            'provider.prescribing',
            'provider.prescribing.location'
        ];

        const fields = [
            'amountPaid',
            'custom.lookup.*',
            'custom.text.*',
            'daysSupply',
            'drug.genericName',
            'identifier.external',
            'perDay',
            'pharmacy.name',
            'pharmacy.phoneNumber.fax.number',
            'pharmacy.phoneNumber.primary.number',
            'provider.prescribing.description',
            'provider.prescribing.location.description',
            'quantity',
            'serviceDate',
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMemberPharmacyClaims(this.memberId, options, includes, fields);
            this.pharmacyClaims = response.results;
            this.calculatePerDays();
            this.configurePharmacyPhoneNumbers();

            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
